import React, { useEffect, useState, type FC, type FormEventHandler } from "react";
import { reportError } from "@app/services/error-reporter";
import { AppDocumentTitle } from "@app/document-title";
import { Svg } from "@shared/ui/svg";
import { useNavigate, useSearchParams } from "react-router-dom";
import { loadAppConfig } from "@app/config/app/appConfig";
import { ompApiClient } from "@app/config/axios/clients/ompApiClient";
import { AppPaths } from "@app/router/paths";
import { resolvePostAuthRedirectPath } from "@app/router/resolvePostAuthRedirect";
import { REDIRECT_QUERY_PARAM } from "@app/router/queryParams";
import { useAuthStore } from "@app/store/useAuthStore";
import { Button, TextField } from "@omega/ui";
import { SettingIcon } from "@omega/icons";
import "./Auth.scss";

const isDev = process.env.NODE_ENV !== "production";

type FormValues = {
    userName: string;
    password: string;
};

const formInputs = [
    {
        name: "userName",
        label: "Логин",
        required: true,
        type: "text",
    },
    {
        name: "password",
        label: "Пароль",
        required: true,
        type: "password",
    },
] as const;

/** Заголовок на странице авторизации */
const AUTH_TITLE = "OmpMobile";

function validate(values: FormValues): Record<keyof FormValues, string | null> {
    return {
        userName: values.userName.trim().length === 0 ? "Логин обязателен" : null,
        password: values.password.length === 0 ? "Пароль обязателен" : null,
    };
}

const Auth: FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated());

    const [values, setValues] = useState<FormValues>({ userName: "", password: "" });
    const [touched, setTouched] = useState<Partial<Record<keyof FormValues, boolean>>>({});
    const [customerName, setCustomerName] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validation = validate(values);
    const isValid = Object.values(validation).every((message) => message === null);
    const redirectPath = resolvePostAuthRedirectPath(searchParams.get(REDIRECT_QUERY_PARAM));

    useEffect(() => {
        if (isAuthenticated) {
            navigate(redirectPath, { replace: true });
        }
    }, [isAuthenticated, navigate, redirectPath]);

    useEffect(() => {
        loadAppConfig()
            .then(({ customer }) => {
                setCustomerName(customer.name);
            })
            .catch((error) => reportError(error, { from: "Auth.loadAppConfig" }));
    }, []);

    const handleChange = (name: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setValues((prev) => ({ ...prev, [name]: event.target.value }));
    };

    const handleBlur = (name: keyof FormValues) => () => {
        setTouched((prev) => ({ ...prev, [name]: true }));
    };

    const submit = async (event?: React.FormEvent) => {
        event?.preventDefault();
        setTouched({ userName: true, password: true });

        const nextValidation = validate(values);
        if (Object.values(nextValidation).some((message) => message !== null)) {
            return;
        }

        setIsSubmitting(true);
        try {
            await ompApiClient.authorizeUser({
                method: "login",
                login: values.userName,
                password: values.password,
            });
            navigate(redirectPath, { replace: true });
        } catch (error) {
            setIsSubmitting(false);
            reportError(error, { from: "Auth.submit" });
        }
    };

    const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
        void submit(event);
    };

    return (
        <div className="auth">
            <AppDocumentTitle text="Авторизация" />
            <div className="auth__card">
                <div className="auth__form-container">
                    <form className="auth__form" onSubmit={onSubmit} noValidate>
                        <div className="auth__logo">
                            <Svg className="auth__logo-mark" src="/images/auth/logoOMP.svg" alt="Логотип" />
                            <div>
                                <div className="auth__title">{AUTH_TITLE}</div>
                                {customerName != null && <div className="auth__subtitle">{customerName}</div>}
                            </div>
                        </div>

                        <div className="auth__inputs" aria-live="polite">
                            {formInputs.map((field) => {
                                const name = field.name;
                                const errorMessage = touched[name] === true ? validation[name] : null;

                                return (
                                    <TextField
                                        key={name}
                                        label={field.label}
                                        id={name}
                                        name={name}
                                        type={field.type}
                                        required={field.required}
                                        value={values[name]}
                                        onChange={handleChange(name)}
                                        onBlur={handleBlur(name)}
                                        invalid={errorMessage != null}
                                        errorMessage={errorMessage ?? undefined}
                                        autoComplete={name === "password" ? "current-password" : "username"}
                                        fullWidth
                                        size="md"
                                        variant="outlined"
                                        labelBehavior="floating"
                                    />
                                );
                            })}
                        </div>

                        <div className="auth__submit">
                            <Button
                                style={{ minWidth: "125px" }}
                                type="submit"
                                tone="accent"
                                size="lg"
                                loading={isSubmitting}
                                disabled={!isValid || isSubmitting}
                            >
                                Войти
                            </Button>
                        </div>
                    </form>
                </div>

                <div className="auth__illus">
                    <div className="auth__illus-figure">
                        <Svg src="/images/auth/rectangle.svg" alt="" />
                    </div>
                </div>
            </div>

            {isDev && (
                <button
                    type="button"
                    className="auth__settings-fab"
                    aria-label="Выбор сервера (настройки)"
                    title="Выбор сервера"
                    onClick={() => navigate(AppPaths.SETTING)}
                >
                    <SettingIcon className="auth__settings-fab-icon" size={22} />
                </button>
            )}
        </div>
    );
};

export default Auth;
